import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SurchargesMaintenance } from '../types';

import { convertBaseForSave, convertDetailsForSave } from '../utils';

import { SURCHARGES_MAINTENANCE } from 'RestAPIs/endpoints';
import { useCreateResource } from 'RestAPIs/useCreateResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { useCreateDetailsMutation } from '../hooks/useCreateDetailsMutation';
import { baseWithDetailsSchema } from '../schema';
import DetailsForm from './DetailsForm';
import { CodeField, NameField } from './FormFields';

type FormProps = {
  handleClose: () => void;
  rowData?: SurchargesMaintenance;
};

const CreateEditSurchargesMaintenanceForm = ({
  handleClose,
  rowData
}: FormProps) => {
  const { t } = useTranslation();
  const { mutateAsync, isLoading: loadBase } = useCreateResource({
    url: SURCHARGES_MAINTENANCE,
    invalidateQueryKey: ['surchargesMaintenance']
  });
  const { mutate: mutateDetails, isLoading: loadDetails } =
    useCreateDetailsMutation(handleClose);

  const methods = useForm({
    defaultValues: baseWithDetailsSchema.cast(
      rowData ?? {
        flags: ['SERVICE_BR_DEFAULT_FLAG'],
        surcharge1Perc: 0,
        surcharge3Perc: 0
      }
    ),
    resolver: yupResolver(baseWithDetailsSchema)
  });
  const { control, handleSubmit } = methods;

  const onSubmit = (data) => {
    mutateAsync(convertBaseForSave(data)).then((response) => {
      mutateDetails(convertDetailsForSave(data, response.data.surchargeId));
    });
  };

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <MultiStepForm.Section
        title={t('surchargesMaintenance.form.basis')}
        validateFields={['code']}
      >
        <CodeField />
        <NameField control={control} />
      </MultiStepForm.Section>

      <MultiStepForm.Section
        title={t('surchargesMaintenance.form.details')}
        validateFields={['method', 'valid.from', 'valid.to']}
      >
        <DetailsForm />
      </MultiStepForm.Section>

      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton
            loading={loadBase || loadDetails}
            type="submit"
            variant="contained"
            autoFocus
            color="dark"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default CreateEditSurchargesMaintenanceForm;
